import request from '@/utils/request';
import queryString from 'query-string';
import { saveAs } from 'file-saver';
import { onLogOut } from '@/store/actions/session.actions';
import pluralize from 'pluralize';
import { apiBaseUrl } from '@/utils/request';

import { XCSRFHeader } from '@/utils/auth';

import { LOG_OUT } from '@/store/actions/session.actions';
import { getLoginStack } from '@/utils/storage';

export const USERS = 'USERS';
export const USER = 'USER';
export const CREATE_USERS = 'CREATE_USERS';
export const CATEGORY = 'CATEGORY';
export const PRODUCTS = 'PRODUCTS';
export const PRODUCT = 'PRODUCT';
export const ADMIN_PAYMENT_INFOS = 'ADMIN_PAYMENT_INFOS';
export const CORPORATIONS = 'CORPORATIONS';
export const CORPORATION = 'CORPORATION';
export const ORDER_CSV = 'ORDER_CSV';
export const ORDER = 'ORDER';
export const BROADCAST_EMAILS = 'BROADCAST_EMAILS';
export const BROADCAST_EMAIL = 'BROADCAST_EMAIL';
export const GROUPS = 'GROUPS';
export const GROUP = 'GROUP';
export const POOL = 'POOL';
export const POOLS = 'POOLS';
export const BILLING_USER_POOLS = 'BILLING_USER_POOLS';
export const POOL_HOLDERS = 'POOL_HOLDERS';
export const SIGNUPS = 'SIGNUPS';
export const BUSINESS_UNITS = 'BUSINESS_UNITS';
export const BUSINESS_UNIT = 'BUSINESS_UNIT';
export const ACTIVE_FEATURE_FLAG = 'ACTIVE_FEATURE_FLAG';
export const CAMPAIGN_TRIGGERS = 'CAMPAIGN_TRIGGERS';
export const CAMPAIGN_TRIGGER = 'CAMPAIGN_TRIGGER';
export const FEATURE_FLAGS = 'FEATURE_FLAGS';
export const LOGIN_TYPES = {
  LOGIN_AS: 'login_as',
  IMPERSONATE: 'impersonate'
};
export const DEFAULT_USER_LIMIT = 20;
export const COMRA_REPORTS = 'COMRA_REPORTS';
export const OFFICE_LOCATIONS = 'OFFICE_LOCATIONS';
export const OFFICE_LOCATION = 'OFFICE_LOCATION';
export const EMAIL_TEMPLATE_QUEUE = 'EMAIL_TEMPLATE_QUEUE';
export const EMAIL_TEMPLATE_QUEUE_ENTRY = 'EMAIL_TEMPLATE_QUEUE_ENTRY';
export const SAVE_AGENCY_USERS = 'SAVE_AGENCY_USERS';
export const CLEAR_AGENCY_USERS = 'CLEAR_AGENCY_USERS';

export const fetchAdminUsers =
  ({ page, limit, q, order, order_field, impersonators, user_id, only_rm }) =>
  async dispatch => {
    const response = await request({
      path: `/v1/admin/users`,
      query: { page, limit, q, order, order_field, impersonators, user_id, only_rm }
    });
    dispatch({
      payload: response,
      type: USERS
    });
    return response;
  };

export const searchAdminUsers =
  ({ page, limit, q, order, order_field, impersonators, user_id, only_rm }) =>
  async dispatch => {
    const response = await request({
      path: `/v1/admin/users/search`,
      method: 'PATCH',
      body: { page, limit, q, order, order_field, impersonators, user_id, only_rm }
    });
    dispatch({
      payload: response,
      type: USERS
    });
    return response;
  };

export const fetchAdminUser = id => async dispatch => {
  const response = await request({
    path: `/v1/admin/users/${id}`
  });
  dispatch({
    payload: response,
    type: USER
  });
};

export const fetchAdminUserRoles = id => async () =>
  await request({
    path: `/v1/admin/users/${id}/roles`
  });

export const updateAdminUserRoles = (id, body) => async () =>
  await request({
    path: `/v1/admin/users/${id}/update_roles`,
    method: 'PATCH',
    body
  });

export const createAdminUser = body => async () => {
  const response = await request({
    path: `/v1/admin/users`,
    method: 'POST',
    body
  });

  return response;
};

export const updateAdminUser = (id, body) => async () =>
  await request({
    path: `/v1/admin/users/${id}`,
    method: 'PATCH',
    body
  });

export const clientflow360Signup = body => async () => {
  const response = await request({
    path: `/v1/users/clientwave360_signup`,
    method: 'POST',
    body
  });
  return response;
};

export const quickUpdateAdminUser = (id, body) => async () =>
  await request({
    path: `/v1/admin/users/${id}/quick_update`,
    method: 'PATCH',
    body
  });

export const loginAsUser =
  (id, { login_type, asAdmin, asRM, asBuAdmin, asManager, asImpersonated }) =>
  async dispatch => {
    await request({
      path: `/v1/admin/users/${id}/login`,
      method: 'POST',
      body: { login_type }
    });
    const stack = getLoginStack();
    if (asRM) {
      stack.push('impersonating_as_rm');
    } else if (asBuAdmin) {
      stack.push('logged_as_bu_admin');
    } else if (asManager) {
      stack.push('logged_as_manager');
    } else if (asAdmin) {
      stack.push('logged_as_admin');
    } else if (asImpersonated) {
      stack.push('impersonating');
    }
    window.location = '/';
    await dispatch({ type: LOG_OUT });
  };

export const logBack = as => async dispatch => {
  getLoginStack().pop();
  await dispatch(onLogOut({ persistLoginStack: true }));
  await request({
    path: `/v1/sessions/${as}`,
    method: 'POST'
  });
  window.location = '/';
};

export const createAdminPaidMembership = (id, body) => async () =>
  await request({
    path: `/v1/admin/users/${id}/paid_membership`,
    method: 'POST',
    body
  });

export const fetchOwners = query => async () =>
  await request({
    path: `/v1/admin/users/owners`,
    query
  });

export const fetchPoolHolders = query => async dispatch => {
  const response = await request({
    path: `/v1/admin/users/pool_holders`,
    query
  });
  dispatch({
    payload: response,
    type: POOL_HOLDERS
  });
};

export const fetchUsersPools = q => async () =>
  await request({
    path: `/v1/pools`,
    query: { q }
  });

export const deleteAdminUser = id => async () =>
  await request({
    path: `/v1/admin/users/${id}`,
    method: 'DELETE'
  });

export const fetchCarriers = q => async () =>
  await request({
    path: `/v1/admin/carriers`,
    query: { q }
  });

export const createCarrier = body => async () =>
  await request({
    path: `/v1/admin/carriers`,
    method: 'POST',
    body
  });

export const updateCarrier = body => async () =>
  await request({
    path: `/v1/admin/carriers/${body.id}`,
    method: 'PATCH',
    body
  });

export const deleteCarrier = id => async () =>
  await request({
    path: `/v1/admin/carriers/${id}`,
    method: 'DELETE'
  });

export const fetchAdminImportSettings = query => async () =>
  await request({
    path: `/v1/admin/import_settings`,
    query
  });

export const fetchAdminImportSetting = id => async () =>
  await request({
    path: `/v1/admin/import_settings/${id}`
  });

export const updateAdminImportSetting = (id, body) => async () =>
  await request({
    path: `/v1/admin/import_settings/${id}`,
    method: 'PATCH',
    body
  });

export const copyAdminImportSetting = id => async () =>
  await request({
    path: `/v1/admin/import_settings/${id}/copy`,
    method: 'POST'
  });

export const deleteAdminImportSetting = id => async () =>
  await request({
    path: `/v1/admin/import_settings/${id}`,
    method: 'DELETE'
  });

export const fetchLeadVendors = q => async () =>
  await request({
    path: `/v1/admin/lead_vendors`,
    query: { q }
  });

export const createLeadVendor = body => async () =>
  await request({
    path: `/v1/admin/lead_vendors`,
    method: 'POST',
    body
  });

export const updateLeadVendor = body => async () =>
  await request({
    path: `/v1/admin/lead_vendors/${body.id}`,
    method: 'PATCH',
    body
  });

export const deleteLeadVendor = id => async () =>
  await request({
    path: `/v1/admin/lead_vendors/${id}`,
    method: 'DELETE'
  });

export const fetchAutoLeadAssignmentSettings = () => async () =>
  await request({
    path: `/v1/admin/auto_lead_assignment_settings`
  });

export const updateAutoLeadAssignmentSettings = body => async () =>
  await request({
    path: `/v1/admin/auto_lead_assignment_settings`,
    method: 'PATCH',
    body: { config: body }
  });

export const fetchAutoLeadAssignmentRules = query => async () =>
  await request({
    path: `/v1/admin/auto_lead_assignment_rules`,
    query
  });

export const createAutoLeadAssignmentRule = body => async () =>
  await request({
    path: `/v1/admin/auto_lead_assignment_rules`,
    method: 'POST',
    body
  });

export const updateAutoLeadAssignmentRule = body => async () =>
  await request({
    path: `/v1/admin/auto_lead_assignment_rules/${body.id}`,
    method: 'PATCH',
    body
  });

export const deleteAutoLeadAssignmentRule = id => async () =>
  await request({
    path: `/v1/admin/auto_lead_assignment_rules/${id}`,
    method: 'DELETE'
  });

export const fetchAutoLeadAssignmentTracks = query => async () =>
  await request({
    path: `/v1/admin/auto_lead_assignment_tracks`,
    query
  });

export const exportAutoLeadAssignmentTracksCsv =
  ({ q } = {}) =>
  async () => {
    const query = {};
    if (q != null && String(q).trim().length > 0) {
      query.q = String(q).trim();
    }
    const url = `${apiBaseUrl()}/v1/admin/auto_lead_assignment_tracks/export?${queryString.stringify(query)}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: Object.assign({ Accept: 'text/csv' }, XCSRFHeader()),
      credentials: 'include'
    });
    if (!response.ok) {
      const err = await response.text().catch(() => '');
      throw new Error(err || response.statusText || 'Export failed');
    }
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition');
    const match = disposition && disposition.match(/filename="?([^";]+)"?/i);
    const filename = match && match[1] ? match[1].trim() : `auto_lead_assignment_tracks.csv`;
    saveAs(blob, filename);
  };

export const fetchAutoLeadAssignmentVendorOptions = () => async () =>
  await request({
    path: `/v1/admin/auto_lead_assignment_rules/vendor_options`
  });

export const fetchAutoLeadAssignmentAgentsGrid =
  ({ q, status, page, limit } = {}) =>
  async () =>
    await request({
      path: `/v1/admin/auto_lead_assignment_rules/by_agent`,
      query: { q, status, page, limit }
    });

export const syncAutoLeadAssignmentAgent = body => async () =>
  await request({
    path: `/v1/admin/auto_lead_assignment_rules/sync_agent`,
    method: 'POST',
    body
  });

export const destroyAutoLeadAssignmentAgent = user_number => async () =>
  await request({
    path: `/v1/admin/auto_lead_assignment_rules/destroy_agent`,
    method: 'DELETE',
    query: { user_number }
  });

export const importAutoLeadAssignmentAgentsCsv = file => async () => {
  const formDataBody = new FormData();
  formDataBody.append('file', file);
  return await request({
    path: `/v1/admin/auto_lead_assignment_rules/import_agents_csv`,
    method: 'POST',
    formData: true,
    formDataBody
  });
};

export const fetchStatuses = q => async () =>
  await request({
    path: `/v1/admin/statuses`,
    query: { q }
  });

export const fetchAgentflowStatuses =
  (business_unit_id = null) =>
  async () =>
    await request({
      path: `/v1/admin/agentflow_statuses`,
      query: business_unit_id ? { business_unit_id } : {}
    });

export const createAgentflowStatus = body => async () =>
  await request({
    path: `/v1/admin/agentflow_statuses`,
    method: 'POST',
    body
  });

export const updateAgentflowStatus = body => async () =>
  await request({
    path: `/v1/admin/agentflow_statuses/${body.id}`,
    method: 'PATCH',
    body
  });

export const updateAgentflowStatusGroup = body => async () =>
  await request({
    path: `/v1/admin/agentflow_status_groups/${body.id}`,
    method: 'PATCH',
    body
  });

export const bulkUpdateAgentflowStatuses = body => async () =>
  await request({
    path: `/v1/admin/agentflow_statuses/bulk_update`,
    method: 'POST',
    body
  });

export const deleteAgentflowStatus = id => async () =>
  await request({
    path: `/v1/admin/agentflow_statuses/${id}`,
    method: 'DELETE'
  });

export const createStatus = body => async () =>
  await request({
    path: `/v1/admin/statuses`,
    method: 'POST',
    body
  });

export const updateStatus = body => async () =>
  await request({
    path: `/v1/admin/statuses/${body.id}`,
    method: 'PATCH',
    body
  });

export const deleteStatus = id => async () =>
  await request({
    path: `/v1/admin/statuses/${id}`,
    method: 'DELETE'
  });

export const fetchSources =
  (q, business_unit_id = null) =>
  async () =>
    await request({
      path: `/v1/admin/sources`,
      query: { q, business_unit_id }
    });

export const createSource = body => async () =>
  await request({
    path: `/v1/admin/sources`,
    method: 'POST',
    body
  });

export const updateSource = body => async () =>
  await request({
    path: `/v1/admin/sources/${body.id}`,
    method: 'PATCH',
    body
  });

export const deleteSource = id => async () =>
  await request({
    path: `/v1/admin/sources/${id}`,
    method: 'DELETE'
  });

export const fetchCategories =
  ({ q }) =>
  async () =>
    await request({
      path: `/v1/admin/categories`,
      query: { q }
    });

export const fetchCategory = id => async dispatch => {
  const response = await request({
    path: `/v1/admin/categories/${id}`
  });
  dispatch({
    payload: response,
    type: CATEGORY
  });
};

export const changeCategory = (id, title, description) => async () =>
  await request({
    path: `/v1/admin/categories/${id}`,
    method: 'PATCH',
    body: { title, description }
  });

export const createCategory = (title, description) => async () =>
  await request({
    path: `/v1/admin/categories`,
    method: 'POST',
    body: { title: title, description: description }
  });

export const deleteCategory = id => async () =>
  await request({
    path: `/v1/admin/categories/${id}`,
    method: 'DELETE'
  });

export const fetchProducts =
  ({ q, page, limit, all, category, type }) =>
  async dispatch => {
    const response = await request({
      path: `/v1/admin/products`,
      query: { q, page, limit, all, category, type }
    });
    dispatch({
      payload: response,
      type: PRODUCTS
    });
    return response;
  };

export const fetchProduct = id => async dispatch => {
  const response = await request({
    path: `/v1/admin/products/${id}`
  });
  dispatch({
    payload: response,
    type: PRODUCT
  });
};

export const editProduct =
  ({
    id,
    name,
    description,
    category_id,
    corporation_id,
    price,
    savings_description,
    external_url,
    months_count,
    active,
    image
  }) =>
  async () => {
    const formDataBody = new FormData();
    formDataBody.append('name', name);
    formDataBody.append('description', description);
    formDataBody.append('category_id', category_id);
    formDataBody.append('corporation_id', corporation_id);
    formDataBody.append('price', price);
    formDataBody.append('savings_description', savings_description);
    formDataBody.append('external_url', external_url);
    months_count && formDataBody.append('months_count', months_count);
    formDataBody.append('active', active);
    image.name && formDataBody.append('image', image);

    await request({
      path: `/v1/admin/products/${id}`,
      method: 'PATCH',
      formData: true,
      formDataBody
    });
  };

export const createProduct =
  ({
    name,
    description,
    category_id,
    corporation_id,
    price,
    savings_description,
    external_url,
    months_count,
    active,
    image
  }) =>
  async () => {
    const formDataBody = new FormData();
    formDataBody.append('name', name);
    formDataBody.append('description', description);
    formDataBody.append('category_id', category_id);
    formDataBody.append('corporation_id', corporation_id);
    formDataBody.append('price', price);
    formDataBody.append('savings_description', savings_description);
    formDataBody.append('external_url', external_url);
    months_count && formDataBody.append('months_count', months_count);
    formDataBody.append('active', active);
    formDataBody.append('image', image);

    await request({
      path: `/v1/admin/products`,
      method: 'POST',
      formData: true,
      formDataBody
    });
  };

export const deleteProduct = id => async () =>
  await request({
    path: `/v1/admin/products/${id}`,
    method: 'DELETE'
  });

export const fetchPaymentInfos =
  ({ user_id, q, all_tabs, tab, all_statuses, status_filter, search_fields, order_field, order, limit, page }) =>
  async dispatch => {
    const response = await request({
      path: `/v1/admin/order_management`,
      query: {
        user_id,
        q,
        all_tabs,
        tab,
        all_statuses,
        status_filter,
        search_fields,
        order_field,
        order,
        limit,
        page
      }
    });
    dispatch({
      payload: response,
      type: ADMIN_PAYMENT_INFOS
    });
  };

export const fetchCorporations =
  ({ q, type, business_unit_ids = [], company_id = '', for_campaign_triggers }) =>
  async dispatch => {
    const response = await request({
      path: `/v1/admin/corporations`,
      query: { q, type, business_unit_ids, company_id, for_campaign_triggers }
    });
    dispatch({
      payload: response,
      type: CORPORATIONS
    });
    return response;
  };

export const fetchCorporation = (id, type) => async dispatch => {
  const response = await request({
    path: `/v1/admin/corporations/${id}`,
    query: { type }
  });
  dispatch({
    payload: response,
    type: CORPORATION
  });
};

export const fetchCorporationUserHierarchy =
  (id, query = {}) =>
  async () => {
    const response = await request({
      path: `/v1/admin/corporations/${id}/user_hierarchy`,
      query
    });
    return response;
  };

const buildCorporationFormData = ({
  type,
  name,
  description = '',
  domain_name,
  color_1,
  color_2,
  color_3,
  files,
  logo,
  business_unit_id = null,
  faqs = [],
  latest_news = [],
  languages_spoken = [],
  office_hours = [],
  office_address = '',
  office_city = '',
  office_state = '',
  office_zip_code = '',
  office_phone = '',
  office_email = '',
  facebook = '',
  instagram = '',
  linkedin = '',
  twitter = '',
  google = '',
  youtube = '',
  yelp = '',
  tracking_code = '',
  webpage_active = false,
  mad_leads = false,
  user_id = null,
  isUpdate = false
}) => {
  const formData = new FormData();

  formData.append('type', type);
  formData.append('name', name);
  formData.append('description', description);
  formData.append('domain_name', domain_name);
  formData.append('color_1', color_1);
  formData.append('color_2', color_2);
  formData.append('color_3', color_3);
  formData.append('faqs', JSON.stringify(faqs));
  formData.append('latest_news', JSON.stringify(latest_news));
  formData.append('business_unit_id', business_unit_id);
  languages_spoken.forEach((el, index) => {
    formData.append(`languages_spoken[][${index}]`, el);
  });
  formData.append('office_hours', JSON.stringify(office_hours));
  formData.append('office_address', office_address);
  formData.append('office_city', office_city);
  formData.append('office_state', office_state);
  formData.append('office_zip_code', office_zip_code);
  formData.append('office_phone', office_phone);
  formData.append('office_email', office_email);
  formData.append('facebook', facebook);
  formData.append('instagram', instagram);
  formData.append('linkedin', linkedin);
  formData.append('twitter', twitter);
  formData.append('google', google);
  formData.append('youtube', youtube);
  formData.append('yelp', yelp);
  formData.append('tracking_code', tracking_code);
  formData.append('webpage_active', webpage_active);
  formData.append('mad_leads', mad_leads);
  formData.append('user_id', user_id);

  if (logo?.name || (!isUpdate && logo)) {
    formData.append('logo', logo);
  }

  files?.forEach((file, index) => {
    if (file.id) formData.append(`files[][${index}][id]`, file.id);
    if (file.file_title) formData.append(`files[][${index}][file_title]`, file.file_title);

    if (file.preview_image?.name) {
      formData.append(`files[][${index}][preview_image]`, file.preview_image);
    }

    if (file.preview_image?.url) {
      formData.append(`files[][${index}][remote_preview_image_url]`, file.preview_image.url);
    }

    if (file.file?.lastModified) {
      formData.append(`files[][${index}][file]`, file.file);
    }

    if (file.file_link) {
      formData.append(`files[][${index}][file_link]`, file.file_link);
    }

    if (file.delete) {
      formData.append(`files[][${index}][delete]`, file.delete);
    }
  });

  return formData;
};

export const createCorporation = params => async () => {
  const formDataBody = buildCorporationFormData({ ...params, isUpdate: false });

  await request({
    path: `/v1/admin/corporations`,
    method: 'POST',
    formData: true,
    formDataBody
  });
};

export const updateCorporation = params => async () => {
  const { id } = params;
  const formDataBody = buildCorporationFormData({ ...params, isUpdate: true });

  await request({
    path: `/v1/admin/corporations/${id}`,
    method: 'PATCH',
    formData: true,
    formDataBody
  });
};

export const deleteCorporation = id => async () =>
  await request({
    path: `/v1/admin/corporations/${id}`,
    method: 'DELETE'
  });

export const exportOrderCSV =
  ({ all, ids, order_type }) =>
  async dispatch => {
    const url = `${apiBaseUrl()}/v1/admin/order_management/export.csv?&all=${all}&${queryString.stringify(
      { ids, order_type },
      { arrayFormat: 'bracket' }
    )}`;

    await fetch(url, {
      headers: Object.assign({ 'Content-Type': 'application/json' }, XCSRFHeader()),
      credentials: 'include',
      responseType: 'blob'
    }).then(response =>
      dispatch({
        payload: response.blob().then(blob => saveAs(blob, `${pluralize('order', ids.length)}.csv`)),
        type: ORDER_CSV
      })
    );
  };

export const fetchOrder = id => async dispatch => {
  const response = await request({
    path: `/v1/admin/order_management/${id}`
  });
  dispatch({
    payload: response,
    type: ORDER
  });
};

export const manageOrder =
  ({ id, amount }) =>
  async () =>
    await request({
      path: `/v1/admin/order_management/${id}/refund`,
      method: 'POST',
      body: { amount }
    });

export const updateOrder =
  ({ id, admin_status }) =>
  async () =>
    await request({
      path: `/v1/admin/order_management/${id}`,
      method: 'PATCH',
      body: { admin_status }
    });

export const fetchBroadcastEmails =
  ({
    q,
    order,
    all,
    page,
    limit,
    template_audience,
    template_product_type,
    template_intent,
    // CF360-only template metadata filters. Other tenants never pass these.
    template_category,
    template_requirements
  }) =>
  async dispatch => {
    const response = await request({
      path: `/v1/admin/emails`,
      query: {
        q,
        order,
        all,
        page,
        limit,
        template_audience,
        template_product_type,
        template_intent,
        template_category,
        template_requirements
      }
    });
    dispatch({
      payload: response,
      type: BROADCAST_EMAILS
    });
    return response;
  };

export const fetchBroadcastEmail =
  ({ id }) =>
  async dispatch => {
    const response = await request({
      path: `/v1/admin/emails/${id}`
    });
    dispatch({
      payload: response,
      type: BROADCAST_EMAIL
    });
  };

export const createBroadcastEmail = body => async () =>
  await request({
    path: `/v1/admin/emails`,
    method: 'POST',
    body
  });

export const updateBroadcastEmail = (id, body) => async () =>
  await request({
    path: `/v1/admin/emails/${id}`,
    method: 'PATCH',
    body
  });

export const deleteBroadcastEmail = id => async () =>
  await request({
    path: `/v1/admin/emails/${id}`,
    method: 'DELETE'
  });

export const bulkUpdateBroadcastEmails = body => async () =>
  await request({
    path: `/v1/admin/emails/bulk_update`,
    method: 'POST',
    body
  });

export const fetchActiveFeatureFlag = () => async dispatch => {
  const response = await request({
    path: `/v1/admin/feature_flags`
  });
  dispatch({
    payload: response,
    type: ACTIVE_FEATURE_FLAG
  });
};

export const fetchFeatureFlags = () => async dispatch => {
  const response = await request({
    path: `/v1/admin/features`
  });
  dispatch({
    payload: response,
    type: FEATURE_FLAGS
  });
};

export const updateFeatureFlag = body => async () => {
  await request({
    path: `/v1/admin/feature_flags`,
    method: 'PUT',
    body
  });
};

export const quietJobs =
  (selected_envs = []) =>
  async () => {
    await request({
      path: `/v1/admin/quiet_jobs`,
      method: 'POST',
      body: { selected_envs }
    });
  };

export const sendHelpQuestion = body => async () =>
  await request({
    path: `/v1/helps`,
    method: 'POST',
    body
  });

export const createGroup = body => async () =>
  await request({
    path: `/v1/groups`,
    method: 'POST',
    body
  });

export const fetchGroups = query => async dispatch => {
  const response = await request({
    path: `/v1/groups`,
    query
  });
  dispatch({
    payload: response,
    type: GROUPS
  });
};

export const fetchGroup = id => async dispatch => {
  const response = await request({
    path: `/v1/groups/${id}`
  });
  dispatch({
    payload: response,
    type: GROUP
  });
};

export const updateGroup = (id, name) => async () =>
  await request({
    path: `/v1/groups/${id}`,
    method: 'PATCH',
    body: { name }
  });

export const deleteGroup = id => async () =>
  await request({
    path: `/v1/groups/${id}`,
    method: 'DELETE'
  });

export const fetchPool = id => async dispatch => {
  const response = await request({
    path: `/v1/pools/${id}`
  });
  dispatch({
    payload: response,
    type: POOL
  });
};

export const createPool = body => async () =>
  await request({
    path: `/v1/pools`,
    method: 'POST',
    body
  });

export const fetchPools = query => async dispatch => {
  const response = await request({
    path: `/v1/pools`,
    query
  });
  dispatch({
    payload: response,
    type: POOLS
  });
};

export const updatePool = (id, body) => async () =>
  await request({
    path: `/v1/pools/${id}`,
    method: 'PATCH',
    body
  });

export const deletePool = id => async () =>
  await request({
    path: `/v1/pools/${id}`,
    method: 'DELETE'
  });

export const createPoolSubscription =
  (body = {}) =>
  async () =>
    await request({
      path: `/v1/pools/create_subscription`,
      method: 'POST',
      body
    });

export const updatePoolSubscription =
  (subscriptionId, body = {}) =>
  async () =>
    await request({
      path: `/v1/pools/update_subscription`,
      method: 'PATCH',
      body: { ...body, id: subscriptionId }
    });

export const cancelPoolSubscription = subscriptionId => async () =>
  await request({
    path: `/v1/pools/cancel_subscription`,
    method: 'PATCH',
    body: { id: subscriptionId }
  });

export const fetchBillingUserPools =
  (userId = null) =>
  async dispatch => {
    const response = await request({ path: `/v1/pools/billing_user_pools`, query: userId ? { user_id: userId } : {} });
    dispatch({ payload: response, type: BILLING_USER_POOLS });
  };

export const fetchSignups = query => async dispatch => {
  const response = await request({
    path: `/v1/admin/signups`,
    query
  });
  dispatch({
    payload: response,
    type: SIGNUPS
  });
};

export const fetchCampaignTriggers =
  ({ q, order, page, limit, all, trigger_type, recurrence, global }) =>
  async dispatch => {
    const response = await request({
      path: `/v1/admin/campaign_triggers`,
      query: { q, order, page, limit, all, trigger_type, recurrence, global }
    });
    dispatch({
      payload: response,
      type: CAMPAIGN_TRIGGERS
    });
    return response;
  };

export const fetchCampaignTrigger =
  ({ id }) =>
  async dispatch => {
    const response = await request({
      path: `/v1/admin/campaign_triggers/${id}`
    });
    dispatch({
      payload: response,
      type: CAMPAIGN_TRIGGER
    });
  };

export const createCampaignTrigger =
  ({ body, file = null }) =>
  async dispatch => {
    const formDataBody = buildCampaignTriggerFormData({ body, file, isUpdate: false });
    await request({
      path: `/v1/admin/campaign_triggers`,
      method: 'POST',
      formData: true,
      formDataBody
    });

    dispatch({
      payload: { data: {} },
      type: CAMPAIGN_TRIGGER
    });
  };

export const updateCampaignTrigger =
  ({ id, body, file = null }) =>
  async dispatch => {
    const formDataBody = buildCampaignTriggerFormData({ body, file, isUpdate: true });
    await request({
      path: `/v1/admin/campaign_triggers/${id}`,
      method: 'PATCH',
      formData: true,
      formDataBody
    });
    dispatch({
      payload: { data: {} },
      type: CAMPAIGN_TRIGGER
    });
  };

const buildCampaignTriggerFormData = ({ body = {}, file, isUpdate = false }) => {
  const formData = new FormData();
  formData.append('name', body.name);
  formData.append('description', body.description);
  formData.append('tags', body.tags);
  formData.append('body', body.body);
  formData.append('trigger_type', body.trigger_type);
  formData.append('category', body.category);
  formData.append('trigger', body.trigger);
  body.enabled !== undefined && formData.append('enabled', body.enabled);
  body.compliant !== undefined && formData.append('compliant', body.compliant);
  body.global !== undefined && formData.append('global', body.global);
  body.auto_trigger !== undefined && formData.append('auto_trigger', body.auto_trigger);
  body.show_on_bulk !== undefined && formData.append('show_on_bulk', body.show_on_bulk);
  body.show_on_conversation !== undefined && formData.append('show_on_conversation', body.show_on_conversation);
  body.show_on_event !== undefined && formData.append('show_on_event', body.show_on_event);
  body.recurrence !== undefined && formData.append('recurrence', body.recurrence);
  body.all_email_ids?.forEach(el => {
    formData.append(`all_email_ids[]`, el);
  });
  body.corporation_ids?.forEach(el => {
    formData.append(`corporation_ids[]`, el);
  });
  body.all_sms_trigger_ids?.forEach(el => {
    formData.append(`all_sms_trigger_ids[]`, el);
  });
  if (file?.name || (!isUpdate && file)) {
    formData.append('file[file]', file);
  }
  return formData;
};

export const deleteCampaignTrigger = id => async () =>
  await request({
    path: `/v1/admin/campaign_triggers/${id}`,
    method: 'DELETE'
  });

export const fetchComraReports = query => async dispatch => {
  const response = await request({
    path: '/v1/admin/comra_leads_reports',
    query
  });
  dispatch({
    payload: response,
    type: COMRA_REPORTS
  });
};

export const fetchOfficeLocations =
  ({ q }) =>
  async dispatch => {
    const response = await request({
      path: `/v1/admin/office_locations`,
      query: { q }
    });
    dispatch({
      payload: response,
      type: OFFICE_LOCATIONS
    });
  };

export const fetchOfficeLocation = id => async dispatch => {
  const response = await request({
    path: `/v1/admin/office_locations/${id}`
  });
  dispatch({
    payload: response,
    type: OFFICE_LOCATION
  });
};

export const createOfficeLocation = data => async () =>
  await request({
    path: `/v1/admin/office_locations`,
    method: 'POST',
    body: data
  });

export const updateOfficeLocation = (id, data) => async () =>
  await request({
    path: `/v1/admin/office_locations/${id}`,
    method: 'PUT',
    body: data
  });

export const deleteOfficeLocation = id => async () =>
  await request({
    path: `/v1/admin/office_locations/${id}`,
    method: 'DELETE'
  });

export const fetchEmailTemplateQueue =
  ({ status, page, limit = 15 }) =>
  async dispatch => {
    const response = await request({
      path: '/v1/email_template_queue',
      method: 'GET',
      query: {
        status: status || undefined,
        page,
        limit
      }
    });
    dispatch({
      payload: response,
      type: EMAIL_TEMPLATE_QUEUE
    });
    return response;
  };

export const createEmailTemplateQueue = body => async () => {
  const response = await request({
    path: '/v1/email_template_queue',
    method: 'POST',
    body: body
  });
  return response;
};

export const deleteEmailTemplateQueue = id => async () =>
  await request({
    path: `/v1/email_template_queue/${id}`,
    method: 'DELETE'
  });

export const approveEmailTemplateQueue = id => async () =>
  await request({
    path: `/v1/email_template_queue/${id}/approve`,
    method: 'POST'
  });

export const rejectEmailTemplateQueue = (id, reason) => async () =>
  await request({
    path: `/v1/email_template_queue/${id}/reject`,
    method: 'POST',
    body: { reason }
  });

export const fetchEmailTemplateQueueEntry = id => async dispatch => {
  const response = await request({
    path: `/v1/email_template_queue/${id}`,
    method: 'GET'
  });
  dispatch({
    payload: response,
    type: EMAIL_TEMPLATE_QUEUE_ENTRY
  });
  return response;
};

export const updateEmailTemplateQueue = (id, body) => async () =>
  await request({
    path: `/v1/email_template_queue/${id}`,
    method: 'PUT',
    body
  });

export const submitEmailTemplateQueue = id => async () =>
  await request({
    path: `/v1/email_template_queue/${id}/submit`,
    method: 'POST'
  });

export const saveAgencyUsers = users => dispatch => {
  dispatch({ payload: users, type: SAVE_AGENCY_USERS });
};

export const clearAgencyUsers = () => dispatch => {
  dispatch({ type: CLEAR_AGENCY_USERS });
};

export const setCashPlan = body => async () =>
  await request({
    path: `/v1/admin/users/cash_plan`,
    method: 'POST',
    body
  });

export const purchasePlan = body => async () =>
  await request({
    path: `/v1/admin/users/purchase_plan`,
    method: 'POST',
    body
  });

export const deactivatePlan = body => async () =>
  await request({
    path: `/v1/admin/users/deactivate_plan`,
    method: 'PATCH',
    body
  });

export const purchaseAddonsForUsers = body => async () =>
  await request({
    path: `/v1/admin/users/purchase_addons`,
    method: 'POST',
    body
  });

export const changeRenewAddon = (id, addon_id) => async () =>
  await request({
    path: `/v1/admin/users/${id}/deactivate_addons_renew`,
    method: 'PATCH',
    body: { addon_id }
  });

export const sendUserInvite = username => async () =>
  await request({
    path: `/v1/users/send_user_invitation`,
    method: 'POST',
    body: { login: username }
  });
